import { ProgressIndicator } from "@asosteam/asos-web-component-library-progress-indicator";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { TextField } from "../text-field";
import styles from "./styles";
import { handleKeyboardNavigation } from "./utilities";
import { useLocaleProvider, useTranslate } from "../../context/locale-provider";

export const FinderField = ({
  className,
  value,
  label,
  ariaLabel,
  sheerIdProgramId,
  sheerIdLocale,
  textFieldId,
  textFieldHasError,
  textFieldErrorMessage,
  textFieldOnChange,
  resultsListId,
  resultsListOnChange,
  resultsItemId,
  fetchOptions,
  required,
}) => {
  const translate = useTranslate(useLocaleProvider());
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState([]);
  const [isFocused, setIsFocused] = useState({
    textField: false,
    results: false,
  });
  const [inputDelayed, setInputDelayed] = useState(false);

  useEffect(() => {
    const getOptions = async () => {
      const response = await fetchOptions();
      if (response?.networkError) {
        setShowResults(false);
        setResults([]);
      } else {
        setResults(response);
      }
    };

    if (value.name.length >= 3 && !inputDelayed) {
      setShowResults(true);
      getOptions();
      setLoading(false);
    } else if (value.name.length < 3) {
      setShowResults(false);
      setLoading(false);
    }
  }, [value, inputDelayed]);

  useEffect(() => {
    if (inputDelayed) {
      setTimeout(() => {
        setInputDelayed(false);
      }, 1000);
    }
  }, [inputDelayed]);

  useEffect(() => {
    setShowResults(
      (isFocused.results || isFocused.textField) && value.name.length >= 3
    );
  }, [isFocused]);

  const onKeyboardNavigation = (e, currentIndex, handleOnClick) => {
    const key = e.which ? e.which : e.keyCode;
    handleKeyboardNavigation(
      e,
      currentIndex,
      handleOnClick,
      setShowResults,
      results,
      key,
      resultsListId,
      resultsItemId
    );
  };

  return (
    <div className={`${styles.wrapper} ${className}`}>
      <label htmlFor={textFieldId} className="app_london3">
        {label}
      </label>
      <TextField
        id={textFieldId}
        ariaLabel={ariaLabel}
        initialValue={value?.name}
        onChange={textFieldOnChange}
        errorMessage={textFieldHasError ? textFieldErrorMessage : ""}
        list={textFieldId}
        className={showResults ? styles.listOpen : ""}
        autocomplete="off"
        onKeyDown={(e) => onKeyboardNavigation(e, -1)}
        onFocus={(e) => {
          e.target.scrollIntoView({
            behavior: "auto",
            block: "center",
            inline: "center",
          });
          setIsFocused({ textField: false, results: true });
        }}
        onBlur={() => {
          setIsFocused({ ...isFocused, textField: false });
        }}
        required={required}
        onKeyUp={() => {
          setInputDelayed(true);
          setLoading(true);
        }}
      />
      {showResults && (
        <>
          <button
            className={`${styles.clear} app_london5`}
            onClick={() => {
              resultsListOnChange &&
                resultsListOnChange({
                  name: "",
                  id: "",
                });
              setShowResults();
            }}
          >
            {translate("sv_form_school_clear")}
          </button>
          <ul
            id={resultsListId}
            className={styles.list}
            onFocus={() => setIsFocused({ textField: false, results: true })}
            onBlur={() => {
              setIsFocused({ ...isFocused, results: false });
            }}
          >
            {loading ? (
              <li className={styles.progressBar}>
                <ProgressIndicator size="small" />
              </li>
            ) : (
              results.map((item, i) => (
                <li
                  className={styles.listItem}
                  key={item.id}
                  id={`${resultsItemId}_${i}`}
                  onClick={async () => {
                    resultsListOnChange && (await resultsListOnChange(item));
                    setShowResults(false);
                  }}
                  tabIndex={-1}
                  onKeyDown={(e) =>
                    onKeyboardNavigation(e, i, () => {
                      resultsListOnChange && resultsListOnChange(item);
                    })
                  }
                  role="menuitem"
                >
                  {item.name}
                </li>
              ))
            )}
            <li
              className={styles.listItem}
              tabIndex={-1}
              id={`${resultsItemId}_${results.length}`}
              onKeyDown={(e) => onKeyboardNavigation(e, results.length)}
              role="menuitem"
            >
              {`${translate("sv_form_school_heading")} `}
              <a
                href={`https://offers.sheerid.com/sheerid/add-school-request/?pid=${sheerIdProgramId}&locale=${sheerIdLocale}`}
              >
                {translate("sv_form_school_req_link")}
              </a>
            </li>
          </ul>
        </>
      )}
    </div>
  );
};

FinderField.propTypes = {
  value: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  sheerIdProgramId: PropTypes.string.isRequired,
  sheerIdLocale: PropTypes.string.isRequired,
  textFieldId: PropTypes.string.isRequired,
  textFieldHasError: PropTypes.bool.isRequired,
  textFieldErrorMessage: PropTypes.string,
  textFieldOnChange: PropTypes.func.isRequired,
  resultsListId: PropTypes.string.isRequired,
  resultsListOnChange: PropTypes.func.isRequired,
  resultsItemId: PropTypes.string.isRequired,
  fetchOptions: PropTypes.func,
  className: PropTypes.string,
  required: PropTypes.bool,
};

FinderField.defaultProps = {
  value: {
    name: "",
    id: "",
  },
  className: "",
  textFieldErrorMessage: "",
};
